import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { Button, Input, Select, Card } from "../../components/ui";
import PROVINCE from "../../utils/province";

// OptionSet da Dataverse
const REGIONI = [
  { value: "100000000", label: "Lombardia" },
  { value: "100000001", label: "Piemonte" },
  { value: "100000002", label: "Veneto" },
  { value: "100000003", label: "Emilia-Romagna" },
  { value: "100000004", label: "Toscana" },
  { value: "100000005", label: "Lazio" },
  { value: "100000006", label: "Campania" },
  { value: "100000007", label: "Sicilia" },
  { value: "100000008", label: "Puglia" },
  { value: "100000009", label: "Liguria" },
  { value: "100000010", label: "Friuli-Venezia Giulia" },
  { value: "100000011", label: "Trentino-Alto Adige" },
  { value: "100000012", label: "Marche" },
  { value: "100000013", label: "Abruzzo" },
  { value: "100000014", label: "Sardegna" },
  { value: "100000015", label: "Calabria" },
  { value: "100000016", label: "Umbria" },
  { value: "100000017", label: "Basilicata" },
  { value: "100000018", label: "Molise" },
  { value: "100000019", label: "Valle d'Aosta" },
];

const STATI_RSA = [
  { value: "100000000", label: "Attiva" },
  { value: "100000001", label: "Inattiva" },
  { value: "100000002", label: "In dismissione" },
];

const TIPOLOGIE_STRUTTURA = [
  { value: "100000000", label: "RSA" },
  { value: "100000001", label: "Casa di Riposo" },
  { value: "100000002", label: "Centro Diurno" },
  { value: "100000003", label: "Comunità Alloggio" },
];

function RsaOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Modalità modifica se presente ID
  const editId = searchParams.get("id");
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [errors, setErrors] = useState({});

  // Form state - tutti i campi dalla tabella phc_rsa
  const [formData, setFormData] = useState({
    // Dati identificativi
    phc_name: "",
    phc_codicersa: "",
    phc_codiceregionale: "",

    // Indirizzo
    phc_indirizzo: "",
    phc_comune: "",
    phc_provincia: "",
    phc_cap: "",
    phc_regione: "",

    // Contatti
    phc_telefonocentralino: "",
    phc_emaildirezione: "",
    phc_emailcondivisionecalendario: "",

    // Referente / Direttore
    phc_nomereferente: "",
    phc_telefonoreferente: "",

    // Configurazione
    phc_stato: "100000000", // Default: Attiva
    phc_tipologiastruttura: "100000000", // Default: RSA
    phc_outlookcalendarid: "",

    // Alias (nomi alternativi per matching email)
    phc_alias: "",

    // Note
    phc_note: "",
  });

  // Carica dati RSA esistente in modalità modifica
  useEffect(() => {
    if (!isEditMode) return;

    const loadRsa = async () => {
      setLoadingData(true);
      try {
        const data = await apiRequest(`/rsa/${editId}`);

        // Popola il form con i dati esistenti
        setFormData({
          phc_name: data.nome || data.phc_name || "",
          phc_codicersa: data.codiceRsa || data.phc_codicersa || "",
          phc_codiceregionale:
            data.codiceRegionale || data.phc_codiceregionale || "",
          phc_indirizzo: data.indirizzo || data.phc_indirizzo || "",
          phc_comune: data.comune || data.phc_comune || "",
          phc_provincia: data.provincia || data.phc_provincia || "",
          phc_cap: data.cap || data.phc_cap || "",
          phc_regione:
            data.regione !== undefined
              ? String(data.regione)
              : data.phc_regione !== undefined
                ? String(data.phc_regione)
                : "",
          phc_telefonocentralino:
            data.telefonoCentralino || data.phc_telefonocentralino || "",
          phc_emaildirezione:
            data.emailDirezione || data.phc_emaildirezione || "",
          phc_emailcondivisionecalendario:
            data.emailCondivisioneCalendario ||
            data.phc_emailcondivisionecalendario ||
            "",
          phc_stato:
            data.stato !== undefined
              ? String(data.stato)
              : data.phc_stato !== undefined
                ? String(data.phc_stato)
                : "100000000",
          phc_tipologiastruttura:
            data.tipologiaStruttura !== undefined
              ? String(data.tipologiaStruttura)
              : data.phc_tipologiastruttura !== undefined
                ? String(data.phc_tipologiastruttura)
                : "100000000",
          phc_outlookcalendarid:
            data.outlookCalendarId || data.phc_outlookcalendarid || "",
          phc_alias: data.alias || data.phc_alias || "",
          phc_note: data.note || data.phc_note || "",
          phc_nomereferente: data.nomeReferente || data.phc_nomereferente || "",
          phc_telefonoreferente:
            data.telefonoReferente || data.phc_telefonoreferente || "",
        });
      } catch (error) {
        console.error("Error loading RSA:", error);
        showToast("Errore nel caricamento della RSA", "error");
        navigate("/rsa");
      } finally {
        setLoadingData(false);
      }
    };

    loadRsa();
  }, [editId, isEditMode, apiRequest, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when field changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.phc_name.trim()) {
      newErrors.phc_name = "Il nome è obbligatorio";
    }

    // Email validation
    if (
      formData.phc_emaildirezione &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.phc_emaildirezione)
    ) {
      newErrors.phc_emaildirezione = "Email non valida";
    }

    // CAP validation
    if (formData.phc_cap && !/^\d{5}$/.test(formData.phc_cap)) {
      newErrors.phc_cap = "CAP non valido (5 cifre)";
    }

    // Phone validation (basic)
    if (
      formData.phc_telefonocentralino &&
      !/^[\d\s\+\-\.]+$/.test(formData.phc_telefonocentralino)
    ) {
      newErrors.phc_telefonocentralino = "Numero di telefono non valido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    setLoading(true);

    try {
      // Prepara i dati per Dataverse
      const dataverseData = {
        ...formData,
        // Converti i picklist in numeri interi
        phc_regione: formData.phc_regione
          ? parseInt(formData.phc_regione)
          : null,
        phc_stato: parseInt(formData.phc_stato),
        phc_tipologiastruttura: parseInt(formData.phc_tipologiastruttura),
      };

      // Rimuovi campi vuoti
      Object.keys(dataverseData).forEach((key) => {
        if (dataverseData[key] === "" || dataverseData[key] === null) {
          delete dataverseData[key];
        }
      });

      if (isEditMode) {
        await apiRequest(`/rsa/${editId}`, {
          method: "PUT",
          body: JSON.stringify(dataverseData),
        });
        showToast("RSA aggiornata con successo!", "success");
      } else {
        await apiRequest("/rsa", {
          method: "POST",
          body: JSON.stringify(dataverseData),
        });
        showToast("RSA creata con successo!", "success");
      }
      navigate("/rsa");
    } catch (error) {
      console.error("Error creating RSA:", error);
      showToast(
        error.message || "Errore durante la creazione della RSA",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/admin" className="hover:text-primary-600">
            Admin
          </a>
          <span>/</span>
          <span>{isEditMode ? "Modifica RSA" : "Nuova RSA"}</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Modifica RSA" : "Registrazione Nuova RSA"}
        </h1>
        <p className="text-gray-500 mt-1">
          {isEditMode
            ? "Modifica i dati della Residenza Sanitaria Assistenziale"
            : "Inserisci i dati della nuova Residenza Sanitaria Assistenziale"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sezione: Dati Identificativi */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏥</span> Dati Identificativi
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Nome RSA *"
                name="phc_name"
                value={formData.phc_name}
                onChange={handleChange}
                placeholder="es. RSA Villa Serena"
                error={errors.phc_name}
                maxLength={200}
              />
            </div>

            <Input
              label="Codice RSA"
              name="phc_codicersa"
              value={formData.phc_codicersa}
              onChange={handleChange}
              placeholder="es. RSA001"
              helperText="Codice interno identificativo"
              maxLength={20}
            />

            <Input
              label="Codice Regionale"
              name="phc_codiceregionale"
              value={formData.phc_codiceregionale}
              onChange={handleChange}
              placeholder="es. 050123"
              helperText="Codice assegnato dalla regione"
              maxLength={50}
            />

            <Select
              label="Tipologia Struttura"
              name="phc_tipologiastruttura"
              value={formData.phc_tipologiastruttura}
              onChange={handleChange}
              options={TIPOLOGIE_STRUTTURA}
            />

            <Select
              label="Stato"
              name="phc_stato"
              value={formData.phc_stato}
              onChange={handleChange}
              options={STATI_RSA}
            />
          </div>
        </Card>

        {/* Sezione: Indirizzo */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📍</span> Indirizzo
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Indirizzo"
                name="phc_indirizzo"
                value={formData.phc_indirizzo}
                onChange={handleChange}
                placeholder="Via/Piazza, numero civico"
                maxLength={200}
              />
            </div>

            <Input
              label="Comune"
              name="phc_comune"
              value={formData.phc_comune}
              onChange={handleChange}
              placeholder="es. Padova"
              maxLength={100}
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Provincia"
                name="phc_provincia"
                value={formData.phc_provincia}
                onChange={handleChange}
                options={PROVINCE}
                placeholder="Seleziona..."
              />

              <Input
                label="CAP"
                name="phc_cap"
                value={formData.phc_cap}
                onChange={handleChange}
                placeholder="35100"
                error={errors.phc_cap}
                maxLength={5}
              />
            </div>

            <Select
              label="Regione"
              name="phc_regione"
              value={formData.phc_regione}
              onChange={handleChange}
              options={REGIONI}
              placeholder="Seleziona..."
            />
          </div>
        </Card>

        {/* Sezione: Contatti */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📞</span> Contatti
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefono Centralino"
              name="phc_telefonocentralino"
              value={formData.phc_telefonocentralino}
              onChange={handleChange}
              placeholder="049 1234567"
              error={errors.phc_telefonocentralino}
              maxLength={20}
            />

            <Input
              label="Email Direzione"
              name="phc_emaildirezione"
              type="email"
              value={formData.phc_emaildirezione}
              onChange={handleChange}
              placeholder="direzione@rsa.it"
              error={errors.phc_emaildirezione}
              maxLength={100}
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Condivisione Calendario
              </label>
              <textarea
                name="phc_emailcondivisionecalendario"
                value={formData.phc_emailcondivisionecalendario}
                onChange={handleChange}
                placeholder="Email separate da virgola per condivisione eventi calendario"
                className="form-input w-full h-20"
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 mt-1">
                Inserire le email dei destinatari delle notifiche calendario,
                separate da virgola
              </p>
            </div>
          </div>
        </Card>

        {/* Sezione: Referente / Direttore */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>👤</span> Referente / Direttore
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Referente"
              name="phc_nomereferente"
              value={formData.phc_nomereferente}
              onChange={handleChange}
              placeholder="es. Dr. Mario Rossi"
              helperText="Nome completo del direttore o referente principale"
              maxLength={200}
            />

            <Input
              label="Telefono Referente"
              name="phc_telefonoreferente"
              value={formData.phc_telefonoreferente}
              onChange={handleChange}
              placeholder="333 1234567"
              helperText="Numero diretto del referente"
              maxLength={20}
            />
          </div>
        </Card>

        {/* Sezione: Configurazione Outlook */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📅</span> Integrazione Calendario
          </h2>

          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Outlook Calendar ID"
              name="phc_outlookcalendarid"
              value={formData.phc_outlookcalendarid}
              onChange={handleChange}
              placeholder="ID del calendario Outlook condiviso"
              helperText="Lasciare vuoto per generazione automatica"
              maxLength={200}
            />
          </div>
        </Card>

        {/* Sezione: Alias / Nomi Alternativi */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏷️</span> Alias / Nomi Alternativi
          </h2>

          <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-1">A cosa servono gli alias?</p>
            <p className="text-blue-700">
              I fornitori potrebbero chiamare la RSA con nomi diversi nelle loro
              email (es. nome della residenza, nome del comune, abbreviazioni).
              Inserisci qui tutti i nomi alternativi separati da virgola per
              permettere al sistema di riconoscere automaticamente la RSA dalle
              email.
            </p>
          </div>

          <textarea
            name="phc_alias"
            value={formData.phc_alias}
            onChange={handleChange}
            placeholder="es. CHIANOC, Residenza Chianoc, Villa Chianoc, RSA Savigliano"
            className="form-input w-full h-24"
            maxLength={2000}
          />
          <p className="text-xs text-gray-500 mt-1">
            Inserire i nomi alternativi separati da virgola
          </p>
        </Card>

        {/* Sezione: Note */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>

          <textarea
            name="phc_note"
            value={formData.phc_note}
            onChange={handleChange}
            placeholder="Note aggiuntive sulla RSA..."
            className="form-input w-full h-32"
            maxLength={10000}
          />
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/admin")}
          >
            ← Annulla
          </Button>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                setFormData({
                  phc_name: "",
                  phc_codicersa: "",
                  phc_codiceregionale: "",
                  phc_indirizzo: "",
                  phc_comune: "",
                  phc_provincia: "",
                  phc_cap: "",
                  phc_regione: "",
                  phc_telefonocentralino: "",
                  phc_emaildirezione: "",
                  phc_emailcondivisionecalendario: "",
                  phc_stato: "100000000",
                  phc_tipologiastruttura: "100000000",
                  phc_outlookcalendarid: "",
                  phc_alias: "",
                  phc_note: "",
                  phc_nomereferente: "",
                  phc_telefonoreferente: "",
                })
              }
            >
              Reset
            </Button>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {isEditMode ? "Salvataggio..." : "Creazione in corso..."}
                </span>
              ) : isEditMode ? (
                "Salva Modifiche"
              ) : (
                "Crea RSA"
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default RsaOnboardingPage;
